/**
 * Photo uploader component for assignment forms (delivery/receiving)
 * Supports up to 12 photos with camera capture and file upload
 * Compresses images and uploads to Supabase Storage
 */

import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Image,
  StyleSheet,
  Alert,
  Platform,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { Ionicons } from '@expo/vector-icons';
import { uploadDamagePhoto, deleteDamagePhoto } from '../../api/storage';
import theme from '../../theme';

interface AssignmentPhotoUploaderProps {
  photos: string[]; // Array of photo URLs
  onPhotosChange: (photos: string[]) => void;
  maxPhotos?: number;
  formType?: 'delivery' | 'receiving' | 'job-card';
}

export const AssignmentPhotoUploader: React.FC<AssignmentPhotoUploaderProps> = ({
  photos,
  onPhotosChange,
  maxPhotos = 12,
  formType = 'receiving',
}) => {
  const [uploading, setUploading] = useState(false);

  // Request camera permission
  const requestCameraPermission = async (): Promise<boolean> => {
    try {
      const { status } = await ImagePicker.requestCameraPermissionsAsync();
      
      if (status !== 'granted') {
        Alert.alert(
          'Permission Required',
          'Camera permission is required to take photos.',
          [{ text: 'OK' }]
        );
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error requesting camera permission:', error);
      return false;
    }
  };

  // Request media library permission
  const requestMediaLibraryPermission = async (): Promise<boolean> => {
    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      
      if (status !== 'granted') {
        Alert.alert(
          'Permission Required',
          'Photo library permission is required to select photos.',
          [{ text: 'OK' }]
        );
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error requesting media library permission:', error);
      return false;
    }
  };

  // Take photo with camera
  const handleTakePhoto = async () => {
    if (photos.length >= maxPhotos) {
      Alert.alert('Limit Reached', `You can only upload up to ${maxPhotos} photos.`);
      return;
    }

    const hasPermission = await requestCameraPermission();
    if (!hasPermission) return;

    try {
      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [4, 3],
        quality: 0.7, // Good balance between quality and file size
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        await uploadPhoto(result.assets[0].uri);
      }
    } catch (error) {
      console.error('Error taking photo:', error);
      Alert.alert('Error', 'Failed to take photo. Please try again.');
    }
  };

  // Pick photo from gallery/file system
  const handlePickPhoto = async () => {
    if (photos.length >= maxPhotos) {
      Alert.alert('Limit Reached', `You can only upload up to ${maxPhotos} photos.`);
      return;
    }

    const hasPermission = await requestMediaLibraryPermission();
    if (!hasPermission) return;

    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [4, 3],
        quality: 0.7, // Compress for faster upload
        allowsMultipleSelection: false, // One at a time for better UX
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        await uploadPhoto(result.assets[0].uri);
      }
    } catch (error) {
      console.error('Error picking photo:', error);
      Alert.alert('Error', 'Failed to select photo. Please try again.');
    }
  };

  // Upload photo to Supabase
  const uploadPhoto = async (uri: string) => {
    try {
      setUploading(true);
      console.log('📸 Uploading photo...');
      
      const url = await uploadDamagePhoto(uri, formType);
      
      console.log('✅ Photo uploaded:', url);
      onPhotosChange([...photos, url]);
      
      if (Platform.OS !== 'web') {
        Alert.alert('Success', 'Photo uploaded successfully');
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      Alert.alert('Error', 'Failed to upload photo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Remove photo
  const handleRemovePhoto = (photoUrl: string, index: number) => {
    Alert.alert(
      'Remove Photo',
      'Are you sure you want to remove this photo?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: async () => {
            try {
              // Remove from storage
              await deleteDamagePhoto(photoUrl);
              
              // Remove from state
              const updatedPhotos = photos.filter((_, i) => i !== index);
              onPhotosChange(updatedPhotos);
              
              console.log('🗑️ Photo removed');
            } catch (error) {
              console.error('Error deleting photo:', error);
              // Still remove from state even if deletion fails
              const updatedPhotos = photos.filter((_, i) => i !== index);
              onPhotosChange(updatedPhotos);
            }
          },
        },
      ]
    );
  };

  // Preview photo in full screen
  const handlePreviewPhoto = (photoUrl: string) => {
    if (Platform.OS === 'web') {
      window.open(photoUrl, '_blank');
    } else {
      // For mobile, you could implement a modal with full-screen image
      Alert.alert('Photo Preview', 'Opening photo...');
      // TODO: Implement full-screen modal for mobile
    }
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>Photos ({photos.length}/{maxPhotos})</Text>
        <Text style={styles.subtitle}>Vehicle damage or documents</Text>
      </View>

      {/* Photo Grid */}
      <ScrollView 
        horizontal={false}
        showsVerticalScrollIndicator={false}
        style={styles.photoGrid}
      >
        <View style={styles.photoRow}>
          {photos.map((photoUrl, index) => (
            <View key={index} style={styles.photoWrapper}>
              <TouchableOpacity
                onPress={() => handlePreviewPhoto(photoUrl)}
                style={styles.photoContainer}
              >
                <Image source={{ uri: photoUrl }} style={styles.photo} />
              </TouchableOpacity>
              
              {/* Remove button */}
              <TouchableOpacity
                onPress={() => handleRemovePhoto(photoUrl, index)}
                style={styles.removeButton}
              >
                <Ionicons name="close-circle" size={24} color="#FF3B30" />
              </TouchableOpacity>
            </View>
          ))}

          {/* Add Photo Buttons */}
          {photos.length < maxPhotos && (
            <View style={styles.addButtonsContainer}>
              {/* Camera Button */}
              {Platform.OS !== 'web' && (
                <TouchableOpacity
                  onPress={handleTakePhoto}
                  style={[styles.addButton, uploading && styles.addButtonDisabled]}
                  disabled={uploading}
                >
                  {uploading ? (
                    <ActivityIndicator size="small" color={theme.colors.primary} />
                  ) : (
                    <>
                      <Ionicons name="camera" size={32} color={theme.colors.primary} />
                      <Text style={styles.addButtonText}>Camera</Text>
                    </>
                  )}
                </TouchableOpacity>
              )}

              {/* Gallery/Upload Button */}
              <TouchableOpacity
                onPress={handlePickPhoto}
                style={[styles.addButton, uploading && styles.addButtonDisabled]}
                disabled={uploading}
              >
                {uploading ? (
                  <ActivityIndicator size="small" color={theme.colors.primary} />
                ) : (
                  <>
                    <Ionicons 
                      name={Platform.OS === 'web' ? 'cloud-upload' : 'images'} 
                      size={32} 
                      color={theme.colors.primary} 
                    />
                    <Text style={styles.addButtonText}>
                      {Platform.OS === 'web' ? 'Upload' : 'Gallery'}
                    </Text>
                  </>
                )}
              </TouchableOpacity>
            </View>
          )}
        </View>
      </ScrollView>

      {/* Help Text */}
      {photos.length === 0 && (
        <Text style={styles.helpText}>
          Add photos to document vehicle condition, damage, or any relevant documents
        </Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  header: {
    marginBottom: 12,
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: '#666',
  },
  photoGrid: {
    maxHeight: 400,
  },
  photoRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  photoWrapper: {
    position: 'relative',
    marginBottom: 12,
  },
  photoContainer: {
    width: 100,
    height: 100,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#F5F5F5',
    borderWidth: 1,
    borderColor: '#DDD',
  },
  photo: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  removeButton: {
    position: 'absolute',
    top: -8,
    right: -8,
    backgroundColor: '#FFF',
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
  },
  addButtonsContainer: {
    flexDirection: 'row',
    gap: 12,
  },
  addButton: {
    width: 100,
    height: 100,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: theme.colors.primary,
    borderStyle: 'dashed',
    backgroundColor: '#F8F9FA',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  addButtonDisabled: {
    opacity: 0.5,
  },
  addButtonText: {
    fontSize: 12,
    color: theme.colors.primary,
    fontWeight: '600',
    marginTop: 4,
  },
  helpText: {
    fontSize: 13,
    color: '#999',
    marginTop: 8,
    fontStyle: 'italic',
  },
});
